import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Link2, PackagePlus } from 'lucide-react';
import { styles, Modal } from '../theme';

import { API_URL, fetchWithAuth } from '../api';
import { usePermissions } from '../context/PermissionContext';
import { formatCurrency } from '../utils/formatUtils';

const emptyForm = {
  mode: 'new', // 'new' or 'link'
  existing_item_id: '',
  name: '',
  pos_category_id: '',
  sold_by: 'each',
  pos_price: '',
  sku: '',
  is_active: 1
};

const PosItems = ({ token, prefillCategoryId, onPrefillConsumed }) => {
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [existingResults, setExistingResults] = useState([]);
  const [existingSearch, setExistingSearch] = useState('');

  const fetchItems = useCallback(async () => {
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        per_page: perPage,
        search: searchQuery,
        pos_category_id: categoryFilter
      });
      const res = await fetchWithAuth(`${API_URL}/pos/items?${queryParams.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setItems(data.data || []);
      setTotalCount(data.total_count || 0);
      setTotalPages(data.total_pages || 0);
    } catch (err) {
      console.error('Error fetching POS items:', err);
    }
  }, [token, currentPage, perPage, searchQuery, categoryFilter]);

  const fetchCategories = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/pos/categories/all`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchCategories(); }, []);

  // Deep-link from Categories' "Create Item" button
  useEffect(() => {
    if (prefillCategoryId) {
      setForm({ ...emptyForm, pos_category_id: prefillCategoryId });
      setEditingId(null);
      setShowForm(true);
      onPrefillConsumed && onPrefillConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillCategoryId]);

  useEffect(() => {
    if (form.mode !== 'link' || !showForm) return;
    const handle = setTimeout(async () => {
      try {
        const res = await fetchWithAuth(`${API_URL}/pos/items/search-existing?q=${encodeURIComponent(existingSearch)}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        setExistingResults(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error searching existing items:', err);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [form.mode, existingSearch, showForm, token]);

  const handleSubmit = async () => {
    if (form.mode === 'link' && !form.existing_item_id) {
      alert('Select an existing item to link');
      return;
    }
    if (form.mode === 'new' && !form.name) {
      alert('Missing required field (Name)');
      return;
    }

    const body = {
      sold_by: form.sold_by,
      pos_price: form.pos_price === '' ? null : form.pos_price,
      pos_category_id: form.pos_category_id || null,
      sku: form.sku || null,
      is_active: form.is_active
    };
    if (editingId) {
      body.name = form.name;
    } else if (form.mode === 'link') {
      body.existing_item_id = form.existing_item_id;
    } else {
      body.name = form.name;
    }

    try {
      const url = editingId ? `${API_URL}/pos/items/${editingId}` : `${API_URL}/pos/items`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        fetchItems();
        setShowForm(false);
        setEditingId(null);
        setForm(emptyForm);
      } else {
        const errData = await res.json();
        alert('Error: ' + (errData.error || JSON.stringify(errData.error)));
      }
    } catch (err) {
      console.error('Error saving item:', err);
    }
  };

  const handleEdit = (item) => {
    setForm({
      mode: 'new',
      existing_item_id: '',
      name: item.item_name || '',
      pos_category_id: item.pos_category_id || '',
      sold_by: item.sold_by || 'each',
      pos_price: item.pos_price === null || item.pos_price === undefined ? '' : item.pos_price,
      sku: item.sku || '',
      is_active: item.is_active === undefined ? 1 : item.is_active
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Remove "${item.item_name}" from POS? The underlying catalog item is kept.`)) return;
    try {
      await fetchWithAuth(`${API_URL}/pos/items/${item.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      fetchItems();
    } catch (err) {
      console.error('Error removing item:', err);
    }
  };

  const formatPrice = (item) => {
    if (item.pos_price === null || item.pos_price === undefined) return 'Enter at sale';
    return `₱${formatCurrency(item.pos_price)}`;
  };

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>POS Items</h1>
        {canCreate('pos_items') && (
          <button onClick={() => { setShowForm(true); setEditingId(null); setForm(emptyForm); setExistingSearch(''); }} style={styles.button}>
            <Plus style={{ width: '20px', height: '20px' }} /> Add Item
          </button>
        )}
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={`${editingId ? 'Edit' : 'Add'} POS Item`}
        maxWidth="550px"
      >
        {!editingId && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <button
              onClick={() => setForm({ ...form, mode: 'new' })}
              style={{
                ...styles.button, ...(form.mode !== 'new' ? styles.buttonSecondary : {}), flex: 1, justifyContent: 'center'
              }}
            >
              <PackagePlus size={16} /> Create New
            </button>
            <button
              onClick={() => setForm({ ...form, mode: 'link' })}
              style={{
                ...styles.button, ...(form.mode !== 'link' ? styles.buttonSecondary : {}), flex: 1, justifyContent: 'center'
              }}
            >
              <Link2 size={16} /> Link Existing Item
            </button>
          </div>
        )}

        {!editingId && form.mode === 'link' ? (
          <div style={styles.formGroup}>
            <label style={styles.label}>Existing Catalog Item *</label>
            <input
              type="text"
              placeholder="Search items already in inventory..."
              value={existingSearch}
              onChange={(e) => setExistingSearch(e.target.value)}
              style={styles.input}
            />
            <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', marginTop: '8px' }}>
              {existingResults.length === 0 ? (
                <div style={{ padding: '12px', fontSize: '13px', color: '#9ca3af' }}>No matches</div>
              ) : existingResults.map(r => (
                <div
                  key={r.id}
                  onClick={() => setForm({ ...form, existing_item_id: r.id, name: r.item_name })}
                  style={{
                    padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                    backgroundColor: String(form.existing_item_id) === String(r.id) ? '#eef2ff' : 'transparent',
                    borderBottom: '1px solid #f3f4f6'
                  }}
                >
                  {r.item_name} <span style={{ color: '#9ca3af' }}>({r.item_code})</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={styles.formGroup}>
            <label style={styles.label}>Name *</label>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={styles.input} />
          </div>
        )}

        <div style={styles.grid2}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Category</label>
            <select value={form.pos_category_id} onChange={(e) => setForm({ ...form, pos_category_id: e.target.value })} style={styles.input}>
              <option value="">No category</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Sold By</label>
            <select value={form.sold_by} onChange={(e) => setForm({ ...form, sold_by: e.target.value })} style={styles.input}>
              <option value="each">Each</option>
              <option value="weight">Weight</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Price</label>
            <input
              type="number"
              step="0.01"
              placeholder="Leave blank to enter at sale"
              value={form.pos_price}
              onChange={(e) => setForm({ ...form, pos_price: e.target.value })}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>SKU</label>
            <input type="text" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} style={styles.input} />
          </div>
        </div>
        {editingId && (
          <div style={styles.formGroup}>
            <label style={styles.label}>
              <input
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
                style={{ marginRight: '8px' }}
              />
              Active
            </label>
          </div>
        )}
        <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button onClick={() => setShowForm(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancel</button>
          <button onClick={handleSubmit} style={styles.button}>Save</button>
        </div>
      </Modal>

      <div style={{ ...styles.card, marginBottom: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Search</label>
          <input
            type="text"
            placeholder="Search by name, code, or SKU..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            style={styles.input}
          />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Category</label>
          <select
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
            style={styles.input}
          >
            <option value="">All Categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      {items.length === 0 ? (
        <div style={{ ...styles.card, textAlign: 'center', color: '#6b7280', padding: '40px' }}>
          No POS items yet.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '16px' }}>
          {items.map(item => (
            <div key={item.id} style={{ ...styles.card, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {item.pos_category_color && (
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: item.pos_category_color, flexShrink: 0 }} />
                )}
                <div style={{ fontWeight: 600, fontSize: '15px', flex: 1 }}>{item.item_name}</div>
                {!item.is_active && <span style={{ fontSize: '11px', color: '#b91c1c' }}>Inactive</span>}
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>{item.pos_category_name || 'No category'}</div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>{item.sold_by === 'weight' ? 'Sold by weight' : 'Sold each'}</div>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatPrice(item)}</div>
              {item.sku && <div style={{ fontSize: '12px', color: '#9ca3af' }}>SKU: {item.sku}</div>}
              <div style={{ display: 'flex', gap: '8px', marginTop: 'auto', justifyContent: 'flex-end' }}>
                {canUpdate('pos_items') && (
                  <button onClick={() => handleEdit(item)} style={{ ...styles.iconButton, color: '#4f46e5' }}>
                    <Edit size={18} />
                  </button>
                )}
                {canDelete('pos_items') && (
                  <button onClick={() => handleDelete(item)} style={{ ...styles.iconButton, color: '#ef4444' }}>
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px', flexWrap: 'wrap' }}>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i + 1}
              onClick={() => setCurrentPage(i + 1)}
              style={{
                padding: '6px 12px', borderRadius: '4px', border: '1px solid #d1d5db',
                backgroundColor: currentPage === i + 1 ? '#4f46e5' : 'white',
                color: currentPage === i + 1 ? 'white' : '#374151',
                fontSize: '14px', cursor: 'pointer'
              }}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
      <div style={{ textAlign: 'center', fontSize: '13px', color: '#9ca3af', marginTop: '12px' }}>
        {totalCount} item(s) total
      </div>
    </div>
  );
};

export default PosItems;
